import React, { useState, useEffect } from 'react';
import { Card, Input, Button, DatePicker, Row, Col, Upload, message, Table, Modal } from 'antd';
import { UploadOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { PageTitle } from '../common';
import { theme } from '../../theme';

interface DatosMuestra {
  key: string;
  testNo?: string;
  subId: string;
  uhml: string;
  ml?: string;
  ui: string;
  str: string;
  elg: string;
  mic: string;
  amt: string;
  rd: string;
  plusB: string;
  tCnt?: string;
  tArea?: string;
  cgRCnt?: string;
  rArea?: string;
  grade?: string;
  leaf?: string;
  mr: string;
  sfi: string;
  // Campos específicos del formato APPA
  sci?: string;
  grado?: string;
  mst?: string;
  mat?: string;
  sf?: string;
  cgrd?: string;
  trCnt?: string;
  trAr?: string;
  trID?: string;
  // Campos específicos del formato Excel
  numero_lote?: string;
  // Campos específicos del formato PREMIER 3RA GENERACIÓN
  gradePercent?: string; // Grade (%) - valores decimales como 0.56, 0.43
  unlabeledColumn?: string; // Columna sin etiqueta - valores como 5, 4, 4
  cg?: string; // C.G - valores como 42-1, 33-3
  rCnt?: string; // r.Cnt - valores como 49, 46, 42
}

interface PadronData {
  id: number;
  apelli: string;
  nomb: string;
  alias: string;
  documento: string;
  nc: string;
  denominacion: string;
  [key: string]: any;
}

const ImportarClasificacion: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [numeroPlanilla, setNumeroPlanilla] = useState<string>('');
  const [fecha, setFecha] = useState<moment.Moment | null>(moment());
  const [archivo, setArchivo] = useState<UploadFile | null>(null);
  const [archivoExcel, setArchivoExcel] = useState<UploadFile | null>(null);
  const [datosMuestras, setDatosMuestras] = useState<DatosMuestra[]>([]);
  const [formatoDetectado, setFormatoDetectado] = useState<string>('');
  const [datosExcelPendientes, setDatosExcelPendientes] = useState<DatosMuestra[]>([]);
  const [habilitarActualizacion, setHabilitarActualizacion] = useState<boolean>(false);
  
  // Estados para Origen y Cliente del Padrón
  const [padronData, setPadronData] = useState<PadronData[]>([]);
  const [loadingPadron, setLoadingPadron] = useState<boolean>(false);
  const [origenSeleccionado, setOrigenSeleccionado] = useState<PadronData | null>(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<PadronData | null>(null);
  const [searchOrigen, setSearchOrigen] = useState('');
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchOrigen, setShowSearchOrigen] = useState(false);
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [observacion, setObservacion] = useState<string>('');
  const [ia, setIa] = useState<string>('');

  // Cargar datos del padrón al iniciar la aplicación
  useEffect(() => {
    const fetchPadron = async () => {
      if (!user?.id_empresa) {
        console.log('id_empresa no está disponible');
        return;
      }

      setLoadingPadron(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<PadronData[]>(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user.id_empresa}/`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setPadronData(response.data);
        console.log('Datos del padrón cargados:', response.data.length, 'registros');
      } catch (error: any) {
        console.error('Error al cargar el padrón:', error);
        message.error('Error al cargar los datos del padrón');
      } finally {
        setLoadingPadron(false);
      }
    };

    fetchPadron();
  }, [user?.id_empresa, getAccessTokenSilently]);

  // Cerrar dropdowns cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.origen-search-container')) {
        setShowSearchOrigen(false);
      }
      if (!target.closest('.cliente-search-container')) {
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOrigenChange = (padron: PadronData | null) => {
    console.log('Origen seleccionado:', padron);
    setOrigenSeleccionado(padron);
    setShowSearchOrigen(false);
  };

  const handleClienteChange = (padron: PadronData | null) => {
    console.log('Cliente seleccionado:', padron);
    setClienteSeleccionado(padron);
    setShowSearchCliente(false);
  };

  const detectarFormatoExcel = (data: any[][]): string => {
    // Buscar en las primeras filas del Excel
    const textoCompleto = data.slice(0, 10).flat().join(' ');
    
    if (textoCompleto.includes('USTER ® HVI 1000') || textoCompleto.includes('Análisis válidos con el reporte de APPA')) {
      return 'USTER_HVI_1000_APPA';
    } else if (textoCompleto.includes('PREMIER HFT 9000')) {
      return 'PREMIER_HFT_9000';
    } else if (textoCompleto.includes('PREMIER ART V1.1.8b')) {
      return 'PREMIER_ART';
    } else if (textoCompleto.includes('Test ID') && textoCompleto.includes('Test Type')) {
      return 'PREMIER_3RA_GEN';
    }
    return 'DESCONOCIDO';
  };

  const detectarFormato = (contenido: string): string => {
    if (contenido.includes('USTER ® HVI 1000') || contenido.includes('Análisis válidos con el reporte de APPA')) {
      return 'USTER_HVI_1000_APPA';
    } else if (contenido.includes('PREMIER HFT 9000')) {
      return 'PREMIER_HFT_9000';
    } else if (contenido.includes('PREMIER ART V1.1.8b')) {
      return 'PREMIER_ART';
    } else if (contenido.includes('Test ID') && contenido.includes('Test Type')) {
      return 'PREMIER_3RA_GEN';
    }
    return 'DESCONOCIDO';
  };

  const parsearExcelAPPA = (data: any[][]) => {
    const muestras: DatosMuestra[] = [];
    
    // Buscar la fila que contiene "Bale ID"
    let inicioDatos = -1;
    for (let i = 0; i < data.length; i++) {
      const fila = data[i];
      if (fila && fila.some(cell => cell && cell.toString().includes('Bale ID'))) {
        inicioDatos = i + 1;
        break;
      }
    }
    
    if (inicioDatos > 0) {
      for (let i = inicioDatos; i < data.length; i++) {
        const fila = data[i];
        if (fila && fila.length >= 19 && fila[0] && !isNaN(Number(fila[0]))) { // Necesitamos hasta columna S (índice 18)
          muestras.push({
            key: `muestra-${i}`,
            testNo: fila[0]?.toString() || '', // A: Bale ID
            subId: fila[0]?.toString() || '', // A: Bale ID también como Sub ID
            sci: fila[2]?.toString() || '', // C: SCI (índice 2)
            grado: '', // GRADO está vacío/no existe
            mst: fila[4]?.toString() || '', // E: MST (índice 4)
            mic: fila[5]?.toString() || '', // F: MIC (índice 5)
            mat: fila[6]?.toString() || '', // G: MAT (índice 6)
            uhml: fila[7]?.toString() || '', // H: UHML (índice 7)
            ui: fila[8]?.toString() || '', // I: UI (índice 8)
            sf: fila[9]?.toString() || '', // J: SF (índice 9)
            str: fila[10]?.toString() || '', // K: STR (índice 10)
            elg: fila[11]?.toString() || '', // L: ELG (índice 11)
            rd: fila[12]?.toString() || '', // M: RD (índice 12)
            plusB: fila[13]?.toString() || '', // N: B (+b) (índice 13)
            cgrd: fila[14]?.toString() || '', // O: CGRD (índice 14)
            trCnt: fila[15]?.toString() || '', // P: TRCNT (índice 15)
            trAr: fila[16]?.toString() || '', // Q: TRAR (índice 16)
            trID: fila[17]?.toString() || '', // R: TrID (índice 17)
            amt: fila[18]?.toString() || '', // S: AMT (índice 18)
            mr: '', // No disponible en este formato
            sfi: fila[9]?.toString() || '', // J: SFI (índice 9) - mismo valor que SF
          });
        }
      }
    }
    
    return muestras;
  };

  const parsearArchivo = (contenido: string) => {
    const lineas = contenido.split('\n');
    const muestras: DatosMuestra[] = [];
    const formato = detectarFormato(contenido);
    
    if (formato === 'USTER_HVI_1000_APPA') {
      // Formato USTER HVI 1000 APPA: buscar líneas después del encabezado y antes de las estadísticas
      let inicioLinea = -1;
      let finLinea = -1;
      
      for (let i = 0; i < lineas.length; i++) {
        // Buscar la línea que contiene "Bale ID" para identificar el inicio de los datos
        if (lineas[i].includes('Bale ID') && lineas[i].includes('SCI')) {
          inicioLinea = i + 1;
        }
        // Buscar el inicio de las estadísticas (línea que contiene "n")
        if (lineas[i].includes('n') && lineas[i].includes('Promedio')) {
          finLinea = i;
          break;
        }
      }
      
      if (inicioLinea > 0 && finLinea > inicioLinea) {
        for (let i = inicioLinea; i < finLinea; i++) {
          const linea = lineas[i].trim();
          if (linea && linea.match(/^\d{6}/)) { // Los Bale ID empiezan con 6 dígitos
            const partes = linea.split(/\s+/);
            if (partes.length >= 18) {
              muestras.push({
                key: `muestra-${i}`,
                testNo: partes[0], // Bale ID como número de test
                subId: partes[0], // Bale ID también como Sub ID
                sci: partes[1], // SCI (puede estar vacío)
                grado: partes[2], // Grado
                mst: partes[3], // Mst [%]
                mic: partes[4], // Mic
                mat: partes[5], // Mat [mat2]
                uhml: partes[6], // UHML [mm]
                ui: partes[7], // UI [%]
                sf: partes[8], // SF [%]
                str: partes[9], // Str [g/tex]
                elg: partes[10], // Elg [%]
                rd: partes[11], // Rd
                plusB: partes[12], // +b
                cgrd: partes[13], // CGrd
                trCnt: partes[14], // TrCnt
                trAr: partes[15], // TrAr [%]
                trID: partes[16], // TrID
                amt: partes[17], // Amt
                mr: '', // No disponible en este formato
                sfi: partes[8], // SFI - mismo valor que SF
              });
            }
          }
        }
      }
      
      setFormatoDetectado('INFORME APPA SANTA FE');
      
      // Extraer información del encabezado
      const loteId = lineas.find(line => line.includes('Lote ID'))?.match(/Lote ID[:\s]+(.+)/)?.[1]?.trim() || '';
      const operador = lineas.find(line => line.includes('Operador'))?.match(/Operador[:\s]+(.+)/)?.[1]?.trim() || '';
      message.info(`Archivo cargado - Lote ID: ${loteId}, Operador: ${operador}, ${muestras.length} muestras encontradas`);
      
    } else if (formato === 'PREMIER_HFT_9000') {
      // Formato PREMIER HFT 9000: buscar líneas después de los guiones y antes de Avg:
      let inicioLinea = -1;
      let finLinea = -1;
      
      for (let i = 0; i < lineas.length; i++) {
        if (lineas[i].includes('------------------') && lineas[i - 1]?.includes('Sl.No.')) {
          inicioLinea = i + 1;
        }
        if (lineas[i].trim().includes('Avg:')) {
          finLinea = i;
          break;
        }
      }
      
      if (inicioLinea > 0 && finLinea > inicioLinea) {
        for (let i = inicioLinea; i < finLinea; i++) {
          const linea = lineas[i].trim();
          if (linea && linea.match(/^\d+/)) {
            const partes = linea.split(/\s+/);
            if (partes.length >= 13) {
              muestras.push({
                key: `muestra-${i}`,
                testNo: partes[0],
                subId: partes[1],
                uhml: partes[2],
                ml: partes[3],
                ui: partes[4],
                mic: partes[5],
                str: partes[6],
                elg: partes[7],
                amt: partes[8],
                rd: partes[9],
                plusB: partes[10],
                cg: partes[11],
                mr: partes[12],
                sfi: partes[13] || '',
              });
            }
          }
        }
      }
      
      setFormatoDetectado('EQUIPO HFT 9000 SEMIAUTOMATICO');
      
      // Extraer información del encabezado
      const identifier = lineas[2]?.match(/Identifier:\s*(\S+)/)?.[1] || '';
      const cotton = lineas[3]?.match(/Cotton:\s*(.+?)\s{2,}/)?.[1]?.trim() || '';
      message.info(`Archivo cargado - Identifier: ${identifier}, Cotton: ${cotton}, ${muestras.length} muestras encontradas`);
      
    } else if (formato === 'PREMIER_ART') {
      // Formato PREMIER ART: buscar líneas después de los guiones y antes de Min:
      let inicioLinea = -1;
      let finLinea = -1;
      
      for (let i = 0; i < lineas.length; i++) {
        if (lineas[i].includes('-------') && inicioLinea === -1) {
          inicioLinea = i + 1;
        }
        if (lineas[i].trim().startsWith('Min:')) {
          finLinea = i;
          break;
        }
      }
      
      if (inicioLinea > 0 && finLinea > inicioLinea) {
        let testNo = 1;
        for (let i = inicioLinea; i < finLinea; i++) {
          const linea = lineas[i].trim();
          if (linea && linea.match(/^\d+/)) {
            const partes = linea.split(/\s+/);
            if (partes.length >= 15) {
              muestras.push({
                key: `muestra-${i}`,
                testNo: testNo.toString(),
                subId: partes[0],
                uhml: partes[1],
                ml: partes[2],
                ui: partes[3],
                str: partes[4],
                elg: partes[5],
                mic: partes[6],
                amt: partes[7],
                rd: partes[8],
                plusB: partes[9],
                cg: partes[10],
                tCnt: partes[11],
                tArea: partes[12],
                leaf: partes[13],
                mr: partes[14],
                sfi: partes[15] || '',
              });
              testNo++;
            }
          }
        }
      }
      
      setFormatoDetectado('EQUIPO PREMIER ART AUTOMATICO');
      
      // Extraer información del encabezado
      const identifier = lineas[2]?.match(/Identifier\s*:\s*(.+?)\s{2,}/)?.[1]?.trim() || '';
      message.info(`Archivo cargado - Identifier: ${identifier}, ${muestras.length} muestras encontradas`);
      
    } else if (formato === 'PREMIER_3RA_GEN') {
      // Formato PREMIER 3RA GENERACION: buscar líneas de datos después del encabezado
      // El formato tiene: Test No, Sub ID, UHML, ML, UI, Str, Elg, Mic, Amt, Rd, +b, C.G.r.Cnt, r.Area, Grade (%), [sin etiqueta], MR, SFI
      let inicioLinea = -1;
      let finLinea = -1;
      
      console.log('=== DEBUGGING PREMIER_3RA_GEN ===');
      console.log('Total líneas:', lineas.length);
      
      // Mostrar todas las líneas para debugging
      for (let i = 0; i < Math.min(lineas.length, 15); i++) {
        console.log(`Línea ${i}:`, lineas[i]);
      }
      
      for (let i = 0; i < lineas.length; i++) {
        // Buscar la línea que contiene los encabezados de las columnas
        // El formato tiene: Test No, Sub ID, UHML, ML, UI, Str, Elg, Mic, Amt, Rd, +b, C.G.r.Cnt, r.Area, Grade (%), [sin etiqueta], MR, SFI
        if (lineas[i].includes('Test No') && lineas[i].includes('Sub ID')) {
          inicioLinea = i + 1;
          console.log('Encontrado encabezado en línea:', i, 'Contenido:', lineas[i]);
        }
        // Buscar el inicio de las estadísticas - ser más específico
        if (lineas[i].includes('Statistics') || (lineas[i].includes('n') && lineas[i].includes('Promedio')) || lineas[i].trim() === '') {
          // Solo marcar como fin si ya encontramos el inicio
          if (inicioLinea > 0) {
            finLinea = i;
            console.log('Encontrado fin en línea:', i, 'Contenido:', lineas[i]);
            break;
          }
        }
      }
      
      console.log('Inicio línea:', inicioLinea, 'Fin línea:', finLinea);
      
      // Si no encontramos el encabezado, intentar detectar líneas de datos directamente
      if (inicioLinea === -1) {
        console.log('No se encontró encabezado, buscando líneas de datos directamente...');
        for (let i = 0; i < lineas.length; i++) {
          const linea = lineas[i].trim();
          // Buscar líneas que empiecen con números y tengan muchos valores
          if (linea && linea.match(/^\d+\s+\d+/) && linea.split(/\s+/).length >= 10) {
            console.log(`Posible línea de datos en ${i}:`, linea);
            if (inicioLinea === -1) {
              inicioLinea = i;
              console.log('Estableciendo inicio en línea:', i);
            }
          }
        }
        // Establecer fin como la última línea
        finLinea = lineas.length;
        console.log('Nuevo inicio línea:', inicioLinea, 'Nuevo fin línea:', finLinea);
      }
      
      if (inicioLinea > 0 && finLinea > inicioLinea) {
        for (let i = inicioLinea; i < finLinea; i++) {
          const linea = lineas[i].trim();
          if (linea && linea.match(/^\d+\s+\d+/)) {
            const partes = linea.split(/\s+/);
            console.log(`Línea ${i}:`, linea);
            console.log('Partes:', partes);
            console.log('Número de partes:', partes.length);
            
            if (partes.length >= 16) {
              const muestra = {
                key: `muestra-${i}`,
                testNo: partes[0],
                subId: partes[1],
                uhml: partes[2],
                ml: partes[3],
                ui: partes[4],
                str: partes[5],
                elg: partes[6],
                mic: partes[7],
                amt: partes[8],
                rd: partes[9],
                plusB: partes[10],
                cg: partes[11], // C.G - valor del campo C.G.r.Cnt (ej: "42-1")
                rCnt: partes[12], // r.Cnt - valor separado (ej: "49")
                rArea: partes[13], // r.Area
                gradePercent: partes[14], // Grade (%) - valores como 5, 4, 4
                unlabeledColumn: '', // Columna sin etiqueta - no existe en este formato
                mr: partes[15], // MR - valores como 0.82, 0.84
                sfi: partes[16] || '', // SFI - valores como 17.4, 15.5
              };
              console.log('Muestra creada:', muestra);
              console.log('Valores específicos:');
              console.log('- rArea (partes[13]):', partes[13]);
              console.log('- gradePercent (partes[14]):', partes[14]);
              console.log('- mr (partes[15]):', partes[15]);
              console.log('- sfi (partes[16]):', partes[16]);
              muestras.push(muestra);
            }
          }
        }
      }
      
      setFormatoDetectado('EQUIPO PREMIER 3RA GENERACION AUTOMATICO');
      
      // Extraer información del encabezado
      const testType = lineas.find(line => line.includes('Test Type'))?.match(/Test Type[:\s]+(.+)/)?.[1]?.trim() || '';
      const testDate = lineas.find(line => line.includes('Test Date'))?.match(/Test Date[:\s]+(.+)/)?.[1]?.trim() || '';
      const remarks = lineas.find(line => line.includes('Remarks'))?.match(/Remarks[:\s]+(.+)/)?.[1]?.trim() || '';
      
      message.info(`Archivo cargado - Test Type: ${testType}, Test Date: ${testDate}, ${muestras.length} muestras encontradas`);
    } else {
      message.error('Formato de archivo no reconocido');
      setFormatoDetectado('FORMATO DESCONOCIDO');
    }
    
    setDatosMuestras(muestras);
  };

  const handleFileChange = (info: any) => {
    if (info.fileList.length > 0) {
      // Validar que Origen y Cliente estén seleccionados
      if (!origenSeleccionado || !clienteSeleccionado) {
        message.error('Debe seleccionar Origen y Cliente antes de cargar el archivo HVI');
        return;
      }
      
      const file = info.fileList[0].originFileObj;
      setArchivo(info.fileList[0]);
      
      // Verificar si es un archivo Excel
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          const formato = detectarFormatoExcel(excelData);
          
          if (formato === 'USTER_HVI_1000_APPA') {
            const muestras = parsearExcelAPPA(excelData);
            setDatosMuestras(muestras);
            setFormatoDetectado('INFORME APPA SANTA FE');
            
            // Extraer información del encabezado
            const loteIdRow = excelData.find((row: any) => 
              row.some((cell: any) => cell && cell.toString().includes('Lote ID'))
            );
            const loteId = loteIdRow?.find((cell: any) => cell && cell.toString().includes('Lote ID'))?.toString().replace('Lote ID', '').trim() || '';
            
            const operadorRow = excelData.find((row: any) => 
              row.some((cell: any) => cell && cell.toString().includes('Operador'))
            );
            const operador = operadorRow?.find((cell: any) => cell && cell.toString().includes('Operador'))?.toString().replace('Operador', '').trim() || '';
            
            message.info(`Archivo Excel cargado - Lote ID: ${loteId}, Operador: ${operador}, ${muestras.length} muestras encontradas`);
          } else {
            message.error('Formato de archivo Excel no reconocido');
            setFormatoDetectado('FORMATO DESCONOCIDO');
          }
        };
        reader.readAsBinaryString(file);
      } else {
        // Leer archivo de texto
        const reader = new FileReader();
        reader.onload = (e) => {
          const contenido = e.target?.result as string;
          parsearArchivo(contenido);
        };
        reader.readAsText(file);
        
        message.success(`${info.file.name} archivo seleccionado correctamente`);
      }
    } else {
      setArchivo(null);
      setDatosMuestras([]);
      setFormatoDetectado('');
      setHabilitarActualizacion(false);
    }
  };

  const handleFileChangeExcel = async (info: any) => {
    if (info.fileList.length > 0) {
      const file = info.fileList[0].originFileObj;
      setArchivoExcel(info.fileList[0]);
      
      // Verificar si es un archivo Excel
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
            
            // Validar que existen datos en las columnas A, B y C
            if (!excelData || excelData.length < 1) {
              message.error('El archivo Excel está vacío');
              return;
            }
            
            // Procesar y validar datos
            const muestras: DatosMuestra[] = [];
            const errores: string[] = [];
            const fardosInvalidos: number[] = [];
            const calidadesInvalidas: number[] = [];
            
            // Iniciar desde línea 0 (datos desde línea 1)
            for (let i = 0; i < excelData.length; i++) {
              const fila = excelData[i];
              const lineaExcel = i + 1;
              
              // Verificar que existen las 3 columnas
              if (!fila || fila.length < 3) {
                errores.push(`Línea ${lineaExcel}: Faltan datos en las columnas A, B o C`);
                continue;
              }
              
              const numeroFardo = fila[0];
              const calidad = fila[1];
              const lote = fila[2];
              
              // Validar que ningún campo esté vacío
              if (numeroFardo === undefined || numeroFardo === null || numeroFardo === '') {
                errores.push(`Línea ${lineaExcel}: Número de Fardo vacío`);
                continue;
              }
              
              if (calidad === undefined || calidad === null || calidad === '') {
                errores.push(`Línea ${lineaExcel}: Calidad vacía`);
                continue;
              }
              
              if (lote === undefined || lote === null || lote === '') {
                errores.push(`Línea ${lineaExcel}: Lote vacío`);
                continue;
              }
              
              // Validar que la calidad sea numérica
              const calidadNumerica = Number(calidad);
              if (isNaN(calidadNumerica)) {
                errores.push(`Línea ${lineaExcel}: La calidad "${calidad}" no es numérica`);
                calidadesInvalidas.push(lineaExcel);
                continue;
              }
              
              // Validar que la calidad esté entre 200 y 700
              if (calidadNumerica < 200 || calidadNumerica > 700) {
                errores.push(`Línea ${lineaExcel}: La calidad ${calidadNumerica} debe estar entre 200 y 700`);
                calidadesInvalidas.push(lineaExcel);
                continue;
              }
              
              muestras.push({
                key: `excel-muestra-${i}`,
                testNo: numeroFardo.toString(),
                subId: numeroFardo.toString(),
                uhml: '',
                ui: '',
                str: '',
                elg: '',
                mic: '',
                amt: '',
                rd: '',
                plusB: '',
                mr: '',
                sfi: '',
                grado: calidadNumerica.toString(),
                numero_lote: lote.toString(),
              });
            }
            
            // Si hay errores de formato, mostrarlos y detener
            if (errores.length > 0) {
              Modal.error({
                title: 'Errores en el archivo Excel',
                content: (
                  <div>
                    <p>Se encontraron los siguientes errores:</p>
                    <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {errores.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                ),
                width: 600,
              });
              return;
            }
            
            // Validar número de planilla
            if (!numeroPlanilla || numeroPlanilla.trim() === '') {
              message.error('Debe ingresar un número de planilla antes de cargar el archivo');
              return;
            }
            
            // Validar que los números de fardo existan en la planilla
            try {
              const token = await getAccessTokenSilently();
              const response = await axios.get(
                `${process.env.REACT_APP_CLASIFICACION_URL}/listar_numero_planilla/${user?.id_empresa}/${numeroPlanilla}`,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              
              const fardosEnPlanilla = response.data.data.map((item: any) => item.numero_fardo.toString());
              const fardosNoEncontrados: string[] = [];
              
              muestras.forEach((muestra) => {
                if (!fardosEnPlanilla.includes(muestra.subId || '')) {
                  fardosNoEncontrados.push(muestra.subId || '');
                }
              });
              
              if (fardosNoEncontrados.length > 0) {
                Modal.error({
                  title: 'Fardos no encontrados en la planilla',
                  content: (
                    <div>
                      <p>Los siguientes fardos no existen en la planilla {numeroPlanilla}:</p>
                      <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {fardosNoEncontrados.map((fardo, idx) => (
                          <li key={idx}>Fardo: {fardo}</li>
                        ))}
                      </ul>
                    </div>
                  ),
                  width: 600,
                });
                return;
              }
              
              // Todo validado correctamente, pedir autorización
              setDatosExcelPendientes(muestras);
              
              Modal.confirm({
                title: 'Confirmación de carga',
                icon: <ExclamationCircleOutlined />,
                content: (
                  <div>
                    <p><strong>Archivo validado correctamente</strong></p>
                    <p>Se encontraron {muestras.length} registros válidos.</p>
                    <p>¿Desea continuar y mostrar los datos?</p>
                  </div>
                ),
                okText: 'Sí, continuar',
                cancelText: 'Cancelar',
                onOk: () => {
                  setDatosMuestras(muestras);
                  setFormatoDetectado('ARCHIVO EXCEL - NumeroFardo, Calidad, Lote');
                  setHabilitarActualizacion(true);
                  message.success(`Archivo cargado exitosamente - ${muestras.length} registros`);
                },
                onCancel: () => {
                  setDatosExcelPendientes([]);
                  setHabilitarActualizacion(false);
                  message.info('Carga cancelada');
                },
              });
              
            } catch (error: any) {
              console.error('Error al validar fardos:', error);
              if (error.response?.status === 404) {
                message.error(`No se encontró la planilla ${numeroPlanilla}`);
              } else {
                message.error('Error al validar los números de fardo');
              }
            }
            
          } catch (error) {
            console.error('Error al procesar el archivo:', error);
            message.error('Error al procesar el archivo Excel');
          }
        };
        reader.readAsBinaryString(file);
      } else {
        message.error('Solo se permiten archivos Excel (.xlsx, .xls)');
      }
    } else {
      setArchivoExcel(null);
      setDatosMuestras([]);
      setFormatoDetectado('');
      setHabilitarActualizacion(false);
    }
  };

  const beforeUpload = (file: File) => {
    // Prevenir la carga automática
    return false;
  };

  const handleActualizarHVI = async () => {
    try {
      // Validar que todos los datos necesarios estén presentes
      if (!numeroPlanilla || numeroPlanilla.trim() === '') {
        message.error('Debe ingresar un número de planilla');
        return;
      }

      if (!fecha) {
        message.error('Debe seleccionar una fecha');
        return;
      }

      if (!origenSeleccionado || !clienteSeleccionado) {
        message.error('Debe seleccionar Origen y Cliente');
        return;
      }

      if (datosMuestras.length === 0) {
        message.error('No hay datos de muestras para enviar');
        return;
      }

      // Preparar los detalles - filtrar registros con nroMuestra vacío
      const detalles = datosMuestras
        .filter((muestra) => muestra.subId && muestra.subId.trim() !== '') // Filtrar nroMuestra vacíos
        .map((muestra) => {
          // Para formato PREMIER 3RA GENERACION, usar gradePercent como calidadNumerica
          let calidadNumerica = '0'; // Default
          
          if (formatoDetectado.includes('PREMIER 3RA GENERACION') && muestra.gradePercent) {
            calidadNumerica = muestra.gradePercent;
          }
          
          return {
            nroMuestra: muestra.subId,
            lote: '0', // Default
            calidad: '', // Default
            calidadNumerica,
            hvi: formatoDetectado
          };
        });

      // Preparar los datos HVI según el formato detectado - filtrar registros con nroMuestra vacío
      const datos_hvi = datosMuestras
        .filter((muestra) => muestra.subId && muestra.subId.trim() !== '') // Filtrar nroMuestra vacíos
        .map((muestra) => {
        // Para SFI: usar sfi si existe y es válido, sino usar sf como alternativa
        const sfiValue = (muestra.sfi && muestra.sfi !== '0') ? muestra.sfi : (muestra.sf || '0');
        
        const datosBase = {
          nroMuestra: muestra.subId,
          mic: muestra.mic || '0',
          uhml: muestra.uhml || '0',
          str: muestra.str || '0',
          ml: muestra.ml || '0',
          ui: muestra.ui || '0',
          elg: muestra.elg || '0',
          amt: muestra.amt || '0',
          rd: muestra.rd || '0',
          b: muestra.plusB || '0',
          mr: muestra.mr || '0',
          sfi: sfiValue
        };

        // Agregar campos específicos según el formato
        if (formatoDetectado.includes('PREMIER 3RA GENERACION')) {
          return {
            ...datosBase,
            cg: muestra.cg || '', // C.G
            r_cnt: muestra.rCnt || '0', // r.Cnt
            r_area: muestra.rArea || '0', // r.Area
            grade_percent: muestra.gradePercent || '0', // Grade (%)
          };
        } else if (formatoDetectado.includes('APPA')) {
          return {
            ...datosBase,
            sci: muestra.sci || '0',
            grado: muestra.grado || '0',
            mst: muestra.mst || '0',
            mat: muestra.mat || '0',
            sf: muestra.sf || '0',
            cgrd: muestra.cgrd || '0',
            tr_cnt: muestra.trCnt || '0',
            tr_ar: muestra.trAr || '0',
            trid: muestra.trID || '0'
          };
        } else if (formatoDetectado.includes('HFT 9000')) {
          return {
            ...datosBase,
            cg: muestra.cg || ''
          };
        } else if (formatoDetectado.includes('PREMIER ART')) {
          return {
            ...datosBase,
            cg: muestra.cg || '',
            t_cnt: muestra.tCnt || '0',
            t_area: muestra.tArea || '0',
            leaf: muestra.leaf || '0'
          };
        }

        return datosBase;
      });

      // Preparar el payload completo
      const payload = {
        empresa_id: user?.id_empresa,
        fecha: fecha.format('YYYY-MM-DD'),
        nro_planilla: numeroPlanilla,
        hvi: formatoDetectado,
        observacion,
        ia,
        origen: origenSeleccionado.id.toString(),
        cliente: clienteSeleccionado.id.toString(),
        detalles,
        datos_hvi
      };

      console.log('=== PAYLOAD A ENVIAR ===');
      console.log('URL:', `${process.env.REACT_APP_CLASIFICACION_URL}/crear_clasificacion/${user?.id_empresa}/HVI/`);
      console.log('Empresa ID:', user?.id_empresa);
      console.log('Fecha:', fecha.format('YYYY-MM-DD'));
      console.log('Número de planilla:', numeroPlanilla);
      console.log('HVI:', formatoDetectado);
      console.log('Observación:', observacion);
      console.log('IA:', ia);
      console.log('Origen seleccionado:', origenSeleccionado?.id, origenSeleccionado?.denominacion);
      console.log('Cliente seleccionado:', clienteSeleccionado?.id, clienteSeleccionado?.denominacion);
      console.log('Formato detectado:', formatoDetectado);
      console.log('Número de detalles:', detalles.length);
      console.log('Número de datos HVI:', datos_hvi.length);
      console.log('Primera muestra HVI:', datos_hvi[0]);
      console.log('Payload completo:', JSON.stringify(payload, null, 2));

      const token = await getAccessTokenSilently();
      
      message.loading({ content: 'Guardando datos HVI...', key: 'guardando_hvi' });
      
      const response = await axios.post(
        `${process.env.REACT_APP_CLASIFICACION_URL}/crear_clasificacion/${user?.id_empresa}/HVI/`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.message) {
        message.success({ 
          content: response.data.message, 
          key: 'guardando_hvi',
          duration: 3 
        });
        
        // Limpiar datos después de guardar exitosamente
        setNumeroPlanilla('');
        setDatosMuestras([]);
        setFormatoDetectado('');
        setArchivo(null);
        setOrigenSeleccionado(null);
        setClienteSeleccionado(null);
        setSearchOrigen('');
        setSearchCliente('');
        setObservacion('');
        setIa('');
      } else {
        message.error({ 
          content: 'Error al guardar los datos HVI', 
          key: 'guardando_hvi',
          duration: 5 
        });
      }
    } catch (error: any) {
      console.error('Error al guardar HVI:', error);
      
      if (error.response?.data?.duplicados) {
        // Mostrar modal con los duplicados
        Modal.error({
          title: 'Muestras Duplicadas',
          content: (
            <div>
              <p>Las siguientes muestras ya existen en el sistema:</p>
              <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {error.response.data.duplicados.map((dup: any, idx: number) => (
                  <li key={idx}>
                    Fardo: {dup.nroMuestra}, Origen: {dup.origen}, Cliente: {dup.cliente}, Lote: {dup.lote}
                  </li>
                ))}
              </ul>
            </div>
          ),
          width: 600,
        });
      } else {
        message.error({ 
          content: error.response?.data?.message || error.response?.data?.error || 'Error al guardar los datos HVI', 
          key: 'guardando_hvi',
          duration: 5 
        });
      }
    }
  };

  const handleActualizarCalidadLote = async () => {
    try {
      // Preparar los datos para enviar
      const datosActualizacion = datosMuestras.map((muestra) => ({
        numero_fardo: parseInt(muestra.subId || '0'),
        calidad: parseInt(muestra.grado || '0'),
        lote: parseInt(muestra.numero_lote || '0')
      }));

      const token = await getAccessTokenSilently();
      
      message.loading({ content: 'Actualizando Calidad y Lote...', key: 'actualizando' });
      
      const response = await axios.post(
        `${process.env.REACT_APP_CLASIFICACION_URL}/actualizar_planilla_clasificacion/${user?.id_empresa}/${numeroPlanilla}/`,
        { datos: datosActualizacion },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      message.destroy('actualizando');

      if (response.data.status === 'success') {
        Modal.success({
          title: 'Actualización Exitosa',
          content: response.data.message,
          okText: 'Aceptar',
          onOk: () => {
            // Limpiar datos después de actualizar
            setNumeroPlanilla('');
            setDatosMuestras([]);
            setFormatoDetectado('');
            setHabilitarActualizacion(false);
            setArchivoExcel(null);
          }
        });
      } else if (response.data.status === 'warning') {
        // Construir mensaje completo para copiar
        let mensajeCompleto = `REPORTE DE ACTUALIZACIÓN - PLANILLA Nº ${numeroPlanilla}\n`;
        mensajeCompleto += `Fecha: ${moment().format('DD/MM/YYYY HH:mm:ss')}\n`;
        mensajeCompleto += `\n${'='.repeat(80)}\n\n`;
        mensajeCompleto += `RESUMEN:\n`;
        mensajeCompleto += `- Registros actualizados: ${response.data.actualizados} de ${response.data.total_procesados}\n`;
        
        if (response.data.lotes_creados && response.data.lotes_creados.length > 0) {
          mensajeCompleto += `- Lotes creados (${response.data.lotes_creados.length}): ${response.data.lotes_creados.join(', ')}\n`;
        }
        
        mensajeCompleto += `- Fardos rechazados: ${response.data.cantidad_errores}\n`;
        mensajeCompleto += `\n${'='.repeat(80)}\n\n`;
        mensajeCompleto += `FARDOS NO ACTUALIZADOS (${response.data.cantidad_errores}):\n\n`;
        
        response.data.errores?.forEach((err: string, idx: number) => {
          mensajeCompleto += `${idx + 1}. ${err}\n`;
        });
        
        mensajeCompleto += `\n${'='.repeat(80)}\n`;
        
        // Función para copiar al portapapeles
        const copiarAlPortapapeles = () => {
          navigator.clipboard.writeText(mensajeCompleto).then(() => {
            message.success('Lista de fardos copiada al portapapeles', 2);
          }).catch(() => {
            message.error('Error al copiar al portapapeles', 2);
          });
        };
        
        // Función para descargar como archivo de texto
        const descargarTxt = () => {
          const blob = new Blob([mensajeCompleto], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Fardos_Rechazados_Planilla_${numeroPlanilla}_${moment().format('YYYYMMDD_HHmmss')}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          message.success('Archivo descargado exitosamente', 2);
        };
        
        Modal.warning({
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>⚠️ Actualización con Advertencias</span>
            </div>
          ),
          width: 800,
          style: { top: 20 },
          content: (
            <div>
              <div style={{
                backgroundColor: theme.colors.warningLight,
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '15px',
                border: `1px solid ${theme.colors.warning}`
              }}>
                <p style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '14px' }}>
                  📊 RESUMEN DE ACTUALIZACIÓN:
                </p>
                <p style={{ margin: '5px 0', fontSize: '13px' }}>
                  ✅ Actualizados: <strong>{response.data.actualizados}</strong> de <strong>{response.data.total_procesados}</strong> registros
                </p>
                {response.data.lotes_creados && response.data.lotes_creados.length > 0 && (
                  <p style={{ margin: '5px 0', fontSize: '13px', color: theme.colors.success }}>
                    🆕 Lotes creados ({response.data.lotes_creados.length}): <strong>{response.data.lotes_creados.join(', ')}</strong>
                  </p>
                )}
                <p style={{ margin: '5px 0', fontSize: '13px', color: theme.colors.error }}>
                  ❌ Fardos rechazados: <strong>{response.data.cantidad_errores}</strong>
                </p>
              </div>
              
              <div style={{ marginBottom: '10px' }}>
                <Button 
                  type="primary" 
                  size="small" 
                  onClick={copiarAlPortapapeles}
                  style={{ marginRight: '8px' }}
                >
                  📋 Copiar Lista
                </Button>
                <Button 
                  type="default" 
                  size="small" 
                  onClick={descargarTxt}
                >
                  💾 Descargar TXT
                </Button>
              </div>
              
              <div style={{
                backgroundColor: theme.colors.errorLight,
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${theme.colors.error}`,
                marginBottom: '10px'
              }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: theme.colors.error, fontSize: '14px' }}>
                  📝 FARDOS NO ACTUALIZADOS ({response.data.cantidad_errores}):
                </p>
                <p style={{ margin: '0', fontSize: '12px', color: theme.colors.textMuted }}>
                  Tome nota de los siguientes números de fardos rechazados:
                </p>
              </div>

              <div style={{
                maxHeight: '400px',
                overflow: 'auto',
                backgroundColor: theme.colors.backgroundGray,
                padding: '12px',
                borderRadius: '4px',
                border: `1px solid ${theme.colors.border}`,
                fontFamily: 'monospace'
              }}>
                {response.data.errores?.map((err: string, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      padding: '6px 8px',
                      marginBottom: '4px',
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : theme.colors.backgroundGray,
                      borderRadius: '3px',
                      fontSize: '13px',
                      borderLeft: `3px solid ${theme.colors.error}`
                    }}
                  >
                    <strong style={{ color: theme.colors.primary }}>{idx + 1}.</strong> {err}
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '12px',
                padding: '8px',
                backgroundColor: theme.colors.infoLight,
                borderRadius: '4px',
                fontSize: '12px',
                color: theme.colors.infoDark
              }}>
                💡 <strong>Sugerencia:</strong> Use el botón "Copiar Lista" o "Descargar TXT" para guardar esta información antes de continuar.
              </div>
            </div>
          ),
          okText: 'Entendido - Cerrar',
          okButtonProps: { 
            size: 'large',
            danger: false,
            type: 'primary'
          },
          onOk: () => {
            // Limpiar datos después de aceptar
            setNumeroPlanilla('');
            setDatosMuestras([]);
            setFormatoDetectado('');
            setHabilitarActualizacion(false);
            setArchivoExcel(null);
          }
        });
      } else {
        Modal.error({
          title: 'Error en la Actualización',
          content: response.data.message,
          okText: 'Aceptar'
        });
      }
    } catch (error: any) {
      console.error('Error al actualizar:', error);
      message.destroy('actualizando');
      
      Modal.error({
        title: 'Error al Actualizar',
        content: error.response?.data?.message || 'Error al actualizar Calidad y Lote',
        okText: 'Aceptar'
      });
    }
  };

  const getColumnas = () => {
    if (formatoDetectado.includes('EXCEL')) {
      // Columnas para formato Excel - NumeroFardo, Calidad, Lote
      return [
        { title: 'NumeroFardo', dataIndex: 'testNo', key: 'testNo', width: 120 },
        { title: 'Calidad', dataIndex: 'grado', key: 'grado', width: 100 },
        { title: 'Lote', dataIndex: 'numero_lote', key: 'numero_lote', width: 100 },
      ];
    } else if (formatoDetectado.includes('APPA')) {
      // Columnas para formato USTER HVI 1000 APPA
      return [
        { title: 'Bale ID', dataIndex: 'testNo', key: 'testNo', width: 90 },
        { title: 'SCI', dataIndex: 'sci', key: 'sci', width: 80 },
        { title: 'Grado', dataIndex: 'grado', key: 'grado', width: 80 },
        { title: 'Mst [%]', dataIndex: 'mst', key: 'mst', width: 80 },
        { title: 'Mic', dataIndex: 'mic', key: 'mic', width: 80 },
        { title: 'Mat [mat2]', dataIndex: 'mat', key: 'mat', width: 90 },
        { title: 'UHML [mm]', dataIndex: 'uhml', key: 'uhml', width: 90 },
        { title: 'UI [%]', dataIndex: 'ui', key: 'ui', width: 80 },
        { title: 'SFI', dataIndex: 'sf', key: 'sf', width: 80 },
        { title: 'Str [g/tex]', dataIndex: 'str', key: 'str', width: 90 },
        { title: 'Elg [%]', dataIndex: 'elg', key: 'elg', width: 80 },
        { title: 'Rd', dataIndex: 'rd', key: 'rd', width: 80 },
        { title: '+b', dataIndex: 'plusB', key: 'plusB', width: 80 },
        { title: 'CGrd', dataIndex: 'cgrd', key: 'cgrd', width: 80 },
        { title: 'TrCnt', dataIndex: 'trCnt', key: 'trCnt', width: 80 },
        { title: 'TrAr [%]', dataIndex: 'trAr', key: 'trAr', width: 80 },
        { title: 'TrID', dataIndex: 'trID', key: 'trID', width: 80 },
        { title: 'Amt', dataIndex: 'amt', key: 'amt', width: 80 },
      ];
    } else if (formatoDetectado.includes('HFT 9000')) {
      // Columnas para formato HFT 9000 (orden: UHML, ML, UI, Mic, Str, Elg, Amt, Rd, +b, CG, MR, SFI)
      return [
        { title: 'Sl.No.', dataIndex: 'testNo', key: 'testNo', width: 80 },
        { title: 'SubID', dataIndex: 'subId', key: 'subId', width: 100 },
        { title: 'UHML', dataIndex: 'uhml', key: 'uhml', width: 90 },
        { title: 'ML', dataIndex: 'ml', key: 'ml', width: 90 },
        { title: 'U.I', dataIndex: 'ui', key: 'ui', width: 80 },
        { title: 'Mic', dataIndex: 'mic', key: 'mic', width: 80 },
        { title: 'Str', dataIndex: 'str', key: 'str', width: 90 },
        { title: 'Elg', dataIndex: 'elg', key: 'elg', width: 80 },
        { title: 'Amt', dataIndex: 'amt', key: 'amt', width: 80 },
        { title: 'Rd', dataIndex: 'rd', key: 'rd', width: 80 },
        { title: '+b', dataIndex: 'plusB', key: 'plusB', width: 80 },
        { title: 'C.G', dataIndex: 'cg', key: 'cg', width: 80 },
        { title: 'MR', dataIndex: 'mr', key: 'mr', width: 80 },
        { title: 'SFI', dataIndex: 'sfi', key: 'sfi', width: 80 },
      ];
    } else if (formatoDetectado.includes('PREMIER ART')) {
      // Columnas para formato PREMIER ART
      return [
        { title: 'Test No', dataIndex: 'testNo', key: 'testNo', width: 80 },
        { title: 'Sub ID', dataIndex: 'subId', key: 'subId', width: 100 },
        { title: 'UHML (mm)', dataIndex: 'uhml', key: 'uhml', width: 90 },
        { title: 'ML (mm)', dataIndex: 'ml', key: 'ml', width: 90 },
        { title: 'UI (%)', dataIndex: 'ui', key: 'ui', width: 80 },
        { title: 'Str (g/tex)', dataIndex: 'str', key: 'str', width: 100 },
        { title: 'Elg (%)', dataIndex: 'elg', key: 'elg', width: 80 },
        { title: 'Mic', dataIndex: 'mic', key: 'mic', width: 80 },
        { title: 'Amt', dataIndex: 'amt', key: 'amt', width: 80 },
        { title: 'Rd', dataIndex: 'rd', key: 'rd', width: 80 },
        { title: '+b', dataIndex: 'plusB', key: 'plusB', width: 80 },
        { title: 'CG', dataIndex: 'cg', key: 'cg', width: 80 },
        { title: 'T.Cnt', dataIndex: 'tCnt', key: 'tCnt', width: 80 },
        { title: 'T.Area', dataIndex: 'tArea', key: 'tArea', width: 80 },
        { title: 'Leaf', dataIndex: 'leaf', key: 'leaf', width: 80 },
        { title: 'MR', dataIndex: 'mr', key: 'mr', width: 80 },
        { title: 'SFI', dataIndex: 'sfi', key: 'sfi', width: 80 },
      ];
    } else {
      // Columnas para formato PREMIER 3RA GENERACION AUTOMATICO
      return [
        { title: 'Test No', dataIndex: 'testNo', key: 'testNo', width: 80 },
        { title: 'Sub ID', dataIndex: 'subId', key: 'subId', width: 100 },
        { title: 'UHML (mm)', dataIndex: 'uhml', key: 'uhml', width: 90 },
        { title: 'ML (mm)', dataIndex: 'ml', key: 'ml', width: 90 },
        { title: 'UI (%)', dataIndex: 'ui', key: 'ui', width: 80 },
        { title: 'Str (g/tex)', dataIndex: 'str', key: 'str', width: 100 },
        { title: 'Elg (%)', dataIndex: 'elg', key: 'elg', width: 80 },
        { title: 'Mic', dataIndex: 'mic', key: 'mic', width: 80 },
        { title: 'Amt', dataIndex: 'amt', key: 'amt', width: 80 },
        { title: 'Rd', dataIndex: 'rd', key: 'rd', width: 80 },
        { title: '+b', dataIndex: 'plusB', key: 'plusB', width: 80 },
        { title: 'C.G', dataIndex: 'cg', key: 'cg', width: 80 },
        { title: 'r.Cnt', dataIndex: 'rCnt', key: 'rCnt', width: 80 },
        { title: 'r.Area', dataIndex: 'rArea', key: 'rArea', width: 80 },
        { title: 'Grade (%)', dataIndex: 'gradePercent', key: 'gradePercent', width: 90 },
        { title: 'MR', dataIndex: 'mr', key: 'mr', width: 80 },
        { title: 'SFI', dataIndex: 'sfi', key: 'sfi', width: 80 },
      ];
    }
  };

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Importar Clasificación</PageTitle>
      <Card
        title="Datos del Formulario"
        style={{ marginBottom: '16px' }}
        headStyle={{ 
          backgroundColor: theme.colors.primary,
          color: 'white',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 600
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 16]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Número de Planilla</div>
          <Input
            value={numeroPlanilla}
            onChange={(e) => setNumeroPlanilla(e.target.value)}
            placeholder="Ingrese el número de planilla"
            size="middle"
            style={{ fontSize: '13px' }}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Fecha</div>
          <DatePicker
            value={fecha}
            onChange={(date) => setFecha(date)}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Seleccione la fecha"
            size="middle"
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>
            Origen <span style={{ color: 'red' }}>*</span>
          </div>
          <div className="origen-search-container" style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Buscar por NC o denominación..."
              style={{
                width: '100%',
                paddingLeft: '28px',
                paddingRight: origenSeleccionado ? '28px' : '10px',
                paddingTop: '5px',
                paddingBottom: '5px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '6px',
                backgroundColor: origenSeleccionado ? theme.colors.backgroundGray : 'white',
                cursor: 'pointer',
                fontSize: '13px',
                height: '32px'
              }}
              value={origenSeleccionado ? `${origenSeleccionado.nc} - ${origenSeleccionado.denominacion}` : searchOrigen}
              onChange={e => {
                setSearchOrigen(e.target.value);
                setOrigenSeleccionado(null);
                setShowSearchOrigen(true);
              }}
              onFocus={() => setShowSearchOrigen(true)}
            />
            {/* Lupa */}
            <SearchOutlined style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
              fontSize: '14px'
            }} />
            {/* Limpiar selección */}
            {origenSeleccionado && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 1100
                }}
                onClick={() => {
                  setOrigenSeleccionado(null);
                  setSearchOrigen('');
                  setShowSearchOrigen(true);
                }}
                title="Limpiar selección"
              >
                ×
              </span>
            )}
            {/* Dropdown */}
            {showSearchOrigen && padronData.length > 0 && !origenSeleccionado && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '180px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginTop: '4px'
              }}>
                {padronData.filter(p => {
                  const search = searchOrigen.trim().toLowerCase();
                  return (
                    p.nc.toLowerCase().includes(search) ||
                    p.denominacion.toLowerCase().includes(search)
                  );
                }).map(padron => (
                  <div
                    key={padron.id}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      textAlign: 'left',
                      fontSize: '13px'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      handleOrigenChange(padron);
                    }}
                  >
                    {padron.nc} - {padron.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>
            Cliente <span style={{ color: 'red' }}>*</span>
          </div>
          <div className="cliente-search-container" style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Buscar por NC o denominación..."
              style={{
                width: '100%',
                paddingLeft: '28px',
                paddingRight: clienteSeleccionado ? '28px' : '10px',
                paddingTop: '5px',
                paddingBottom: '5px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '6px',
                backgroundColor: clienteSeleccionado ? theme.colors.backgroundGray : 'white',
                cursor: 'pointer',
                fontSize: '13px',
                height: '32px'
              }}
              value={clienteSeleccionado ? `${clienteSeleccionado.nc} - ${clienteSeleccionado.denominacion}` : searchCliente}
              onChange={e => {
                setSearchCliente(e.target.value);
                setClienteSeleccionado(null);
                setShowSearchCliente(true);
              }}
              onFocus={() => setShowSearchCliente(true)}
            />
            {/* Lupa */}
            <SearchOutlined style={{
              position: 'absolute',
              left: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
              fontSize: '14px'
            }} />
            {/* Limpiar selección */}
            {clienteSeleccionado && (
              <span
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  zIndex: 1100
                }}
                onClick={() => {
                  setClienteSeleccionado(null);
                  setSearchCliente('');
                  setShowSearchCliente(true);
                }}
                title="Limpiar selección"
              >
                ×
              </span>
            )}
            {/* Dropdown */}
            {showSearchCliente && padronData.length > 0 && !clienteSeleccionado && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '180px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginTop: '4px'
              }}>
                {padronData.filter(p => {
                  const search = searchCliente.trim().toLowerCase();
                  return (
                    p.nc.toLowerCase().includes(search) ||
                    p.denominacion.toLowerCase().includes(search)
                  );
                }).map(padron => (
                  <div
                    key={padron.id}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      textAlign: 'left',
                      fontSize: '13px'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                    onClick={() => {
                      handleClienteChange(padron);
                    }}
                  >
                    {padron.nc} - {padron.denominacion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Archivo HVI</div>
          <Upload
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            maxCount={1}
            fileList={archivo ? [archivo] : []}
            disabled={!origenSeleccionado || !clienteSeleccionado}
          >
            <Button 
              icon={<UploadOutlined />}
              disabled={!origenSeleccionado || !clienteSeleccionado}
              size="middle"
              style={{ fontSize: '13px' }}
            >
              Examinar
            </Button>
          </Upload>
          {(!origenSeleccionado || !clienteSeleccionado) && (
            <div style={{
              marginTop: '6px',
              fontSize: '11px',
              color: theme.colors.error
            }}>
              * Seleccione Origen y Cliente primero
            </div>
          )}
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Archivo Excel (Fardo, Calidad, Lote)</div>
          <Upload
            beforeUpload={beforeUpload}
            onChange={handleFileChangeExcel}
            maxCount={1}
            fileList={archivoExcel ? [archivoExcel] : []}
          >
            <Button icon={<UploadOutlined />} size="middle" style={{ fontSize: '13px' }}>Examinar</Button>
          </Upload>
          <div style={{ 
            marginTop: '6px', 
            padding: '6px 8px', 
            backgroundColor: theme.colors.successLight,
            border: `1px solid ${theme.colors.success}`,
            borderRadius: '4px',
            fontSize: '11px',
            color: theme.colors.successDark,
            lineHeight: '1.4'
          }}>
            <strong>Formato:</strong> Col A: Fardo | Col B: Calidad | Col C: Lote | Desde línea 1
          </div>
        </Col>

        {/* Campos de Observacion e IA */}
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>Observación</div>
          <Input
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            placeholder="Ingrese observación (máximo 255 caracteres)"
            maxLength={255}
            showCount
            size="middle"
            style={{ fontSize: '13px' }}
          />
        </Col>

        <Col xs={24} sm={12}>
          <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>IA</div>
          <Input
            value={ia}
            onChange={(e) => setIa(e.target.value)}
            placeholder="Ingrese IA (máximo 255 caracteres)"
            maxLength={255}
            showCount
            size="middle"
            style={{ fontSize: '13px' }}
          />
        </Col>
      </Row>

      {/* Mostrar formato detectado */}
      {formatoDetectado && (
        <Row style={{ marginTop: '12px' }}>
          <Col span={24}>
            <div style={{
              padding: '10px 12px',
              backgroundColor: theme.colors.infoLight,
              borderLeft: `4px solid ${theme.colors.info}`,
              marginBottom: '12px',
              fontSize: '13px'
            }}>
              <strong>Formato detectado:</strong> {formatoDetectado}
            </div>
          </Col>
        </Row>
      )}

      {/* Mostrar tabla de datos */}
      {datosMuestras.length > 0 && (
        <Card
          title={
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              Datos de las Muestras ({datosMuestras.length} muestras)
            </div>
          }
          style={{ marginTop: '16px', marginBottom: '16px' }}
          headStyle={{
            padding: '8px 16px',
            fontSize: '14px',
            backgroundColor: theme.colors.primary,
            color: 'white'
          }}
          bodyStyle={{ padding: '12px' }}
        >
          <Table
            columns={getColumnas()}
            dataSource={datosMuestras}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
              pageSizeOptions: ['10', '25', '50', '100'],
              position: ['bottomRight']
            }}
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
            size="middle"
          />
        </Card>
      )}

      {/* Botones de Acción */}
      <Row style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <Col xs={24} sm={12} md={10} lg={8}>
          <Button
            type="primary"
            size="middle"
            disabled={!(datosMuestras.length > 0 && !formatoDetectado.includes('EXCEL') && origenSeleccionado && clienteSeleccionado)}
            onClick={handleActualizarHVI}
            style={{
              backgroundColor: (datosMuestras.length > 0 && !formatoDetectado.includes('EXCEL') && origenSeleccionado && clienteSeleccionado) ? theme.colors.primary : undefined,
              borderColor: (datosMuestras.length > 0 && !formatoDetectado.includes('EXCEL') && origenSeleccionado && clienteSeleccionado) ? theme.colors.primary : undefined,
              width: '100%',
              height: '36px',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            Actualizar HVI
          </Button>
        </Col>
        
        <Col xs={24} sm={12} md={10} lg={8}>
          <Button
            type="primary"
            size="middle"
            disabled={!habilitarActualizacion}
            onClick={handleActualizarCalidadLote}
            style={{
              backgroundColor: habilitarActualizacion ? theme.colors.primary : undefined,
              borderColor: habilitarActualizacion ? theme.colors.primary : undefined,
              width: '100%',
              height: '36px',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            Actualizar Calidad - Lote
          </Button>
        </Col>
      </Row>
      </Card>
    </div>
  );
};

export default ImportarClasificacion;

